import React, { useCallback, useEffect, useRef, useState } from "react";
import { benchmarkRequestApis } from "../../api/benchmarkRequestApis";

const STATUS_LABELS = {
  pending: "Pending review",
  in_progress: "In progress",
  completed: "Completed",
  cancelled: "Cancelled",
};

function companyKey(c) {
  return c.wikidataId || c.lei || `${c.name}|${c.country}`.toLowerCase();
}

function CompanyLogo({ company }) {
  const [failed, setFailed] = useState(false);
  if (!company.logo || failed) {
    return (
      <span className="company-logo company-logo--fallback" aria-hidden="true">
        {company.name.charAt(0).toUpperCase()}
      </span>
    );
  }
  return (
    <img
      className="company-logo"
      src={company.logo}
      alt=""
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
}

export default function RequestBenchmarkPanel({ report }) {
  const [peerSuggestions, setPeerSuggestions] = useState([]);
  const [peerSector, setPeerSector] = useState(null);
  const [loadingPeers, setLoadingPeers] = useState(false);
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState([]);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [myRequests, setMyRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);

  const loadMine = useCallback(async () => {
    setLoadingRequests(true);
    try {
      const rows = await benchmarkRequestApis.listMine();
      setMyRequests(rows);
    } catch {
      setMyRequests([]);
    } finally {
      setLoadingRequests(false);
    }
  }, []);

  useEffect(() => {
    loadMine();
  }, [loadMine]);

  const reportSector = report?.sector || "";
  useEffect(() => {
    if (!reportSector) {
      setPeerSuggestions([]);
      setPeerSector(null);
      return undefined;
    }
    let cancelled = false;
    setLoadingPeers(true);
    benchmarkRequestApis
      .suggestCompanies(reportSector, 50)
      .then(({ sector, companies }) => {
        if (cancelled) return;
        setPeerSector(sector);
        setPeerSuggestions(companies);
      })
      .catch(() => {
        if (!cancelled) setPeerSuggestions([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingPeers(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reportSector]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setSuggestions([]);
      setSearching(false);
      return undefined;
    }

    setSearching(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const companies = await benchmarkRequestApis.searchCompanies(q, 12);
        setSuggestions(companies);
        setDropdownOpen(true);
      } catch {
        setSuggestions([]);
      } finally {
        setSearching(false);
      }
    }, 350);

    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const addCompany = (company) => {
    const key = companyKey(company);
    if (selected.some((c) => companyKey(c) === key)) return;
    if (selected.length >= 20) {
      setError("You can select at most 20 companies.");
      return;
    }
    setSelected((prev) => [...prev, company]);
    setQuery("");
    setSuggestions([]);
    setDropdownOpen(false);
    setError("");
  };

  const removeCompany = (key) => {
    setSelected((prev) => prev.filter((c) => companyKey(c) !== key));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selected.length) {
      setError("Select at least one company from the suggestions or search results.");
      return;
    }
    setSubmitting(true);
    setError("");
    setMessage("");
    try {
      const res = await benchmarkRequestApis.create({
        companies: selected,
        notes,
        reportId: report?.id || report?._id || null,
        reportName: report?.name || "",
        reportYear: report?.year ?? null,
      });
      setMessage(
        res?.message ||
          "Request submitted. Our team will process it manually and follow up."
      );
      setSelected([]);
      setNotes("");
      await loadMine();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to submit benchmark request"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="benchmark-panel request-benchmark-panel">
      <h2>Request peer-company benchmarking</h2>
      <p className="request-benchmark-panel__intro">
        Search and select companies to benchmark against. Requests are queued for
        the GE3S team and processed manually — results are not generated
        instantly.
      </p>
      <p className="request-benchmark-panel__source">
        Company data from{" "}
        <a
          href="https://www.wikidata.org/wiki/Wikidata:Main_Page"
          target="_blank"
          rel="noreferrer"
        >
          Wikidata
        </a>{" "}
        (CC0).
      </p>

      {reportSector ? (
        <div className="peer-suggestions">
          <span className="request-benchmark-label">
            Suggested peers{peerSector ? ` · ${peerSector}` : ""}
          </span>
          {loadingPeers ? (
            <p className="request-benchmark-muted">
              Loading leading companies in your sector…
            </p>
          ) : peerSuggestions.length === 0 ? (
            <p className="request-benchmark-muted">
              No sector suggestions available — search for any company below.
            </p>
          ) : (
            <ul className="peer-suggestions__grid">
              {peerSuggestions.map((c) => {
                const key = companyKey(c);
                const already = selected.some((s) => companyKey(s) === key);
                return (
                  <li key={key}>
                    <button
                      type="button"
                      className={`peer-chip${already ? " is-selected" : ""}`}
                      onClick={() =>
                        already ? removeCompany(key) : addCompany(c)
                      }
                      title={[c.industry, c.country].filter(Boolean).join(" · ")}
                      aria-pressed={already}
                    >
                      <CompanyLogo company={c} />
                      <span className="peer-chip__text">
                        <span className="peer-chip__name">{c.name}</span>
                        {c.country ? (
                          <span className="peer-chip__meta">{c.country}</span>
                        ) : null}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      ) : null}

      <form onSubmit={handleSubmit} className="request-benchmark-form">
        <label className="request-benchmark-label">
          Search companies
          <div className="company-search" ref={wrapRef}>
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => suggestions.length > 0 && setDropdownOpen(true)}
              placeholder="Search any company worldwide (min. 2 characters)"
              autoComplete="off"
              className="company-search__input"
            />
            {searching ? (
              <span className="company-search__hint">Searching…</span>
            ) : null}
            {dropdownOpen && suggestions.length > 0 ? (
              <ul className="company-search__dropdown" role="listbox">
                {suggestions.map((c) => {
                  const key = companyKey(c);
                  const already = selected.some((s) => companyKey(s) === key);
                  return (
                    <li key={key}>
                      <button
                        type="button"
                        disabled={already}
                        onClick={() => addCompany(c)}
                        className="company-search__option"
                      >
                        <CompanyLogo company={c} />
                        <span className="company-search__text">
                          <span className="company-search__name">{c.name}</span>
                          <span className="company-search__meta">
                            {[c.industry, c.country, c.domain]
                              .filter(Boolean)
                              .join(" · ")}
                          </span>
                          {c.description ? (
                            <span className="company-search__desc">
                              {c.description}
                            </span>
                          ) : null}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : dropdownOpen && !searching && query.trim().length >= 2 ? (
              <ul className="company-search__dropdown">
                <li className="company-search__empty">
                  No matching companies found on Wikidata.
                </li>
              </ul>
            ) : null}
          </div>
        </label>

        {selected.length > 0 ? (
          <div className="selected-companies">
            <span className="request-benchmark-label">
              Selected ({selected.length}/20)
            </span>
            <ul>
              {selected.map((c) => {
                const key = companyKey(c);
                return (
                  <li key={key}>
                    <div>
                      <strong>{c.name}</strong>
                      <span>
                        {[c.industry, c.country, c.lei ? `LEI ${c.lei}` : ""]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </div>
                    <button type="button" onClick={() => removeCompany(key)}>
                      Remove
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}

        <label className="request-benchmark-label">
          Notes for the team (optional)
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            maxLength={2000}
            placeholder="e.g. Focus on Scope 1–2 intensity and water stewardship vs these peers"
          />
        </label>

        {report?.name ? (
          <p className="request-benchmark-report">
            Linked report: <strong>{report.name}</strong>
            {report.year ? ` (${report.year})` : ""}
          </p>
        ) : null}

        {error ? <p className="request-benchmark-error">{error}</p> : null}
        {message ? <p className="request-benchmark-success">{message}</p> : null}

        <button
          type="submit"
          className="request-benchmark-submit"
          disabled={submitting || !selected.length}
        >
          {submitting ? "Submitting…" : "Submit benchmarking request"}
        </button>
      </form>

      <div className="request-benchmark-history">
        <h3>Your requests</h3>
        {loadingRequests ? (
          <p className="request-benchmark-muted">Loading…</p>
        ) : myRequests.length === 0 ? (
          <p className="request-benchmark-muted">No requests yet.</p>
        ) : (
          <ul>
            {myRequests.map((req) => (
              <li key={req.id}>
                <div className="request-benchmark-history__head">
                  <span
                    className={`request-status request-status--${req.status}`}
                  >
                    {STATUS_LABELS[req.status] || req.status}
                  </span>
                  <time dateTime={req.createdAt}>
                    {req.createdAt
                      ? new Date(req.createdAt).toLocaleString()
                      : ""}
                  </time>
                </div>
                <p className="request-benchmark-history__companies">
                  {(req.companies || []).map((c) => c.name).join(", ")}
                </p>
                {req.notes ? (
                  <p className="request-benchmark-muted">{req.notes}</p>
                ) : null}
                {req.adminNotes ? (
                  <p className="request-benchmark-admin-notes">
                    Team update: {req.adminNotes}
                  </p>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
